import type { CatalogContext, ConfigState, CompatibilityResult, RuleConflict } from "./types";

/**
 * Evaluates all catalog rules against the current selection state.
 * Returns a CompatibilityResult with methods for querying item availability
 * and a fixed-point computed set of auto-selected items.
 *
 * Money: N/A (rule evaluation only).
 * Fixed-point iteration for `requires` is bounded by items.length + 1 to
 * guarantee termination even if rules contain cycles.
 */
export function evaluateRules(
  ctx: CatalogContext,
  state: ConfigState,
): CompatibilityResult {
  const { rules } = ctx;
  const selected = state.selectedItems;

  // ── Fixed-point: compute auto-selected set from `requires` rules ──
  // Start from the user-selected set, then cascade requires outward.
  const autoSelected = new Set<string>();
  let frontier = new Set<string>(selected);
  const maxIterations = ctx.items.length + 1;

  for (let i = 0; i < maxIterations; i++) {
    const newlyAdded = new Set<string>();
    for (const rule of rules) {
      if (rule.ruleType !== "requires") continue;
      if (frontier.has(rule.sourceItemId) && !frontier.has(rule.targetItemId)) {
        newlyAdded.add(rule.targetItemId);
      }
    }
    if (newlyAdded.size === 0) break;
    for (const id of newlyAdded) {
      frontier.add(id);
      // Only track items that weren't in the original selection
      if (!selected.has(id)) {
        autoSelected.add(id);
      }
    }
  }

  // The effective selection (user + auto-required)
  const effective = frontier;

  // ── Compute disabled reasons and conflicts from `excludes` rules ──
  const disabledReasons = new Map<string, string>();
  const conflicts: RuleConflict[] = [];

  for (const rule of rules) {
    if (rule.ruleType !== "excludes") continue;

    const sourceSelected = effective.has(rule.sourceItemId);
    const targetSelected = effective.has(rule.targetItemId);

    if (sourceSelected && targetSelected) {
      // Both selected = conflict
      conflicts.push({
        itemAId: rule.sourceItemId,
        itemBId: rule.targetItemId,
        ruleId: rule.id,
      });
    } else if (sourceSelected && !targetSelected) {
      // Source selected disables target
      if (!disabledReasons.has(rule.targetItemId)) {
        disabledReasons.set(
          rule.targetItemId,
          `Incompatible with selected item`,
        );
      }
    } else if (targetSelected && !sourceSelected) {
      // Target selected disables source
      if (!disabledReasons.has(rule.sourceItemId)) {
        disabledReasons.set(
          rule.sourceItemId,
          `Incompatible with selected item`,
        );
      }
    }
  }

  // ── Apply `enables` rules ──
  // Pass 1: collect all enables rules per target item.
  const enablesByTarget = new Map<string, (typeof rules)[number][]>();
  for (const rule of rules) {
    if (rule.ruleType !== "enables") continue;
    const list = enablesByTarget.get(rule.targetItemId) ?? [];
    list.push(rule);
    enablesByTarget.set(rule.targetItemId, list);
  }

  // Pass 2: for each item with incoming enables rules, apply gate + priority override.
  for (const [targetId, enables] of enablesByTarget) {
    const hasActiveEnabler = enables.some((r) => frontier.has(r.sourceItemId));

    // Find all currently-firing excludes against this target.
    const activeExcludes = rules.filter(
      (r) =>
        r.ruleType === "excludes" &&
        r.targetItemId === targetId &&
        frontier.has(r.sourceItemId),
    );

    if (activeExcludes.length > 0) {
      const highestExcludePriority = activeExcludes.reduce(
        (max, r) => Math.max(max, r.priority ?? 0),
        0,
      );
      const overridingEnables = enables.filter(
        (r) =>
          frontier.has(r.sourceItemId) &&
          (r.priority ?? 0) > highestExcludePriority,
      );
      if (overridingEnables.length > 0) {
        // Higher-priority enable beats the exclude.
        disabledReasons.delete(targetId);
        // Also drop conflicts entries between this target and excluded sources.
        for (let i = conflicts.length - 1; i >= 0; i--) {
          const c = conflicts[i];
          if (
            c &&
            (c.itemAId === targetId || c.itemBId === targetId) &&
            activeExcludes.some((r) => r.id === c.ruleId)
          ) {
            conflicts.splice(i, 1);
          }
        }
      }
    }

    // Even without excludes, an item with `enables` rules pointing to it is gated:
    // requires at least one active enabler (unless it's already selected — symmetric with legacy).
    if (!disabledReasons.has(targetId) && !hasActiveEnabler) {
      disabledReasons.set(targetId, "Requires an enabler to be selected");
    }
  }

  // ── Selected items are always deselectable (#696) ──
  // We deliberately do NOT lock the sole selected item of a default-anchored
  // category. A previous "default-anchored" lock (audit A2-X1) tried to keep
  // such a category non-empty by making its last occupant non-deselectable,
  // but that produced a hard deadlock: when every alternative in the category
  // mutually excludes the selected default (e.g. CPL Basket Type / Vehicle),
  // the user could neither deselect the default nor pick an alternative.
  // Letting the default be deselected frees the `excludes` gating the
  // alternatives, so the user can switch. The "don't persist an empty
  // required category" concern, if needed, belongs at save time — not as a
  // click-time lock.

  // ── Build related-items lookup ──
  const relatedItems = new Map<string, Set<string>>();
  for (const rule of rules) {
    if (!relatedItems.has(rule.sourceItemId)) {
      relatedItems.set(rule.sourceItemId, new Set());
    }
    if (!relatedItems.has(rule.targetItemId)) {
      relatedItems.set(rule.targetItemId, new Set());
    }
    relatedItems.get(rule.sourceItemId)!.add(rule.targetItemId);
    relatedItems.get(rule.targetItemId)!.add(rule.sourceItemId);
  }

  return {
    autoSelected,
    conflicts,

    isAvailable(id: string): boolean {
      return !disabledReasons.has(id);
    },

    canToggle(id: string): boolean {
      // Selected items can always be deselected (#696). Unselected items can
      // be selected only when available (not disabled by an excludes/enables
      // gate).
      if (selected.has(id)) return true;
      return !disabledReasons.has(id);
    },

    getDisabledReason(id: string): string | null {
      return disabledReasons.get(id) ?? null;
    },

    getRelatedItems(id: string): ReadonlyArray<string> {
      return Array.from(relatedItems.get(id) ?? []);
    },
  };
}
